import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, FilterX } from 'lucide-react';
import ActionMenu from '../../components/common/ActionMenu';
import ColumnSettingsModal from '../../components/common/ColumnSettingsModal';
import exportReportExcel from '../reports/export/reportExcelExporter';
import { useAuth } from '../../context/AuthContext';
import { useDataTransfer } from '../../context/DataTransferContext';
import { userActivityApi } from '../../services/api';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';

const RANGES = [
  { label: 'Today',      value: 'today' },
  { label: 'This Week',  value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'This Year',  value: 'year' },
];

const parseRecipe = (exportRecipe) => {
  try { return exportRecipe ? (typeof exportRecipe === 'string' ? JSON.parse(exportRecipe) : exportRecipe) : null; }
  catch { return null; }
};

const formatParamValue = (val) => {
  if (val === null || val === undefined) return '—';
  if (Array.isArray(val)) {
    return val.map(item =>
      item && typeof item === 'object' ? (item.display ?? item.value ?? String(item)) : String(item)
    ).join(', ');
  }
  if (typeof val === 'object') return val.display ?? val.value ?? JSON.stringify(val);
  return String(val);
};

const ActivityHistoryPage = () => {
  const { currentUser } = useAuth();

  const [range, setRange]               = useState('today');
  const [search, setSearch]             = useState('');
  const [rows, setRows]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [filters, setFilters]           = useState({});
  const [columnOrder, setColumnOrder]   = useState(null);
  const [hiddenColumns, setHiddenColumns]     = useState([]);
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  const { registerActivity, updateActivity, completeActivity, failActivity } = useDataTransfer();

  const fetchHistory = useCallback((showSpinner = false) => {
    if (!currentUser?.id) return;
    if (showSpinner) setLoading(true);
    userActivityApi.getHistory(String(currentUser.id), range)
      .then(res => setRows(res.data || []))
      .catch(() => {})
      .finally(() => { if (showSpinner) setLoading(false); });
  }, [currentUser?.id, range]);

  useEffect(() => {
    fetchHistory(true);
    const interval = setInterval(() => fetchHistory(false), 10000);
    return () => clearInterval(interval);
  }, [fetchHistory]);

  const handleDownload = useCallback(async (searchFiltered) => {
    const activityId = `download-data-transfer-history-${Date.now()}`;
    const exportCols = [
      { field: 'name',        label: 'Name' },
      { field: 'format',      label: 'Format' },
      { field: 'type',        label: 'Type' },
      { field: 'status',      label: 'Status' },
      { field: '_fileName',   label: 'File Name' },
      { field: '_database',   label: 'Database' },
      { field: 'userName',    label: 'User' },
      { field: 'machineName', label: 'Machine' },
      { field: '_time',       label: 'Time' },
    ];
    registerActivity(activityId, { name: 'Data Transfer History', type: 'download', format: 'excel', phase: 'Preparing…', pct: 0 });
    try {
      await exportReportExcel({
        config: { reportName: 'Data Transfer History' },
        columns: exportCols,
        data: searchFiltered,
        isCancelled: () => false,
        onProgress: (msg) => updateActivity(activityId, { phase: msg || 'Building Excel…' }),
      });
      completeActivity(activityId);
    } catch {
      failActivity(activityId);
    }
  }, [registerActivity, updateActivity, completeActivity, failActivity]);

  const processedRows = useMemo(() => rows.map(row => {
    const recipe = parseRecipe(row.exportRecipe);
    const ts = row.createdAt ? new Date(row.createdAt) : null;
    return {
      ...row,
      _fileName: recipe?.fileName || '',
      _database: recipe?.dbName || recipe?.dbSlug || '',
      _params: recipe?.filterParams ? JSON.stringify(recipe.filterParams) : '',
      _time: ts
        ? ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' ' +
          ts.toLocaleDateString([], { month: 'short', day: 'numeric' })
        : '',
      _recipe: recipe,
    };
  }), [rows]);

  const searchFiltered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return processedRows;
    return processedRows.filter(r =>
      r.name?.toLowerCase().includes(q) ||
      r.format?.toLowerCase().includes(q) ||
      r.machineName?.toLowerCase().includes(q) ||
      r.status?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q) ||
      r._fileName.toLowerCase().includes(q) ||
      r._database.toLowerCase().includes(q) ||
      r._params.toLowerCase().includes(q)
    );
  }, [processedRows, search]);

  const hasActiveFilters = Object.keys(filters).some(k => filters[k]?.length > 0);

  const columns = useMemo(() => [
    {
      key: 'name', label: 'Name', width: 220,
      renderCell: (row) => <span className="stui-fw-semibold">{row.name}</span>,
    },
    {
      key: 'format', label: 'Format', width: 80,
      renderCell: (row) => (
        <span className="stui-text-italic">{row.format?.toUpperCase() || '—'}</span>
      ),
    },
    {
      key: 'type', label: 'Type', width: 90,
      renderCell: (row) => (
        <span className="stui-text-capitalize">{row.type || '—'}</span>
      ),
    },
    {
      key: 'status', label: 'Status', width: 100,
      renderCell: (row) => (
        <span className={`stui-status-${row.status}`}>{row.status}</span>
      ),
    },
    {
      key: '_fileName', label: 'File Name', width: 180,
      renderCell: (row) => (
        <span className="stui-text-sm-muted">{row._fileName || '—'}</span>
      ),
    },
    {
      key: '_params', label: 'Parameters', width: 180,
      renderCell: (row) => {
        const params = row._recipe?.filterParams;
        const entries = params
          ? Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')
          : [];
        if (entries.length === 0) return <span className="stui-c-muted">—</span>;
        return (
          <span className="stui-text-sm-muted">
            {entries.map(([k, v]) => `${k}: ${formatParamValue(v)}`).join(', ')}
          </span>
        );
      },
    },
    {
      key: '_database', label: 'Database', width: 130,
      renderCell: (row) => (
        <span className="stui-text-sm-muted">{row._database || '—'}</span>
      ),
    },
    {
      key: 'userName', label: 'User', width: 110,
      renderCell: (row) => (
        <span className="stui-text-sm-muted">{row.userName || '—'}</span>
      ),
    },
    {
      key: 'machineName', label: 'Machine', width: 120,
      renderCell: (row) => (
        <span className="stui-text-sm-muted">{row.machineName || '—'}</span>
      ),
    },
    {
      key: '_time', label: 'Time', width: 140,
      renderCell: (row) => (
        <span className="stui-text-sm-muted stui-text-nowrap">{row._time || '—'}</span>
      ),
    },
  ], []);

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <div className="stui-pill-group">
            {RANGES.map(r => (
              <button
                key={r.value}
                className={`stui-pill${range === r.value ? ' selected' : ''}`}
                onClick={() => setRange(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <span className="stui-module-header-meta">
            {searchFiltered.length} record{searchFiltered.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="stui-module-header-actions">
          {hasActiveFilters && (
            <button onClick={() => setFilters({})} title="Clear all column filters" className="stui-btn-ghost">
              <FilterX size={16} />
            </button>
          )}
          <div className="stui-search-wrapper">
            <Search size={16} className="stui-search-icon" style={{ pointerEvents: 'none' }} />
            <input
              type="text"
              className="stui-search-input"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <ActionMenu
            onRefresh={() => fetchHistory(true)}
            onDownload={() => handleDownload(searchFiltered)}
            onModifyView={() => setShowColumnSettings(true)}
            showModifyView
          />
        </div>
      </div>

      {loading ? (
        <div className="stui-module-loading">Loading...</div>
      ) : (
        <div className="stui-datasheet-view">
          <FilterableDataGrid
            data={searchFiltered}
            allData={processedRows}
            columns={columns}
            filters={filters}
            setFilters={setFilters}
            columnOrder={columnOrder}
            hiddenColumns={hiddenColumns}
          />
        </div>
      )}

      <ColumnSettingsModal
        isOpen={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        onApply={({ columnOrder: newOrder, hiddenColumns: newHidden }) => {
          setColumnOrder(newOrder);
          setHiddenColumns(newHidden);
          setShowColumnSettings(false);
        }}
      />
    </div>
  );
};

export default ActivityHistoryPage;
